import React, { forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericSettingsPane, GenericSettingsPaneHandle } from './GenericSettingsPane';
import { PaneHandle, PaneProps } from '../accountParameter.types';
import { useStoreState } from '../../../../models/hooks';

export const CandidatePane = forwardRef<PaneHandle, PaneProps>(({ onSave, setDirty, showActions }, ref) => {
  const { t } = useTranslation('accountParameter');
  const genericPaneRef = useRef<GenericSettingsPaneHandle>(null);
  const account = useStoreState((state) => state.app.accounts);

  useImperativeHandle(ref, () => ({
    save: () => genericPaneRef.current?.save(),
    reset: () => genericPaneRef.current?.reset(),
  }));

  const items = useMemo(
    () => [
      {
        key: 'resumeEnabled',
        label: t('candidatePane.resumeEnabled') as string,
        description: t('candidatePane.resumeEnabledDescription') as string,
        value: account?.configuration?.resumeEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'virtualRecruiterEnabled',
        label: t('candidatePane.virtualRecruiterEnabled') as string,
        description: t('candidatePane.virtualRecruiterEnabledDescription') as string,
        value: account?.configuration?.virtualRecruiterEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'languageEvaluationEnabled',
        label: t('candidatePane.languageEvaluationEnabled') as string,
        description: t('candidatePane.languageEvaluationEnabledDescription') as string,
        value: account?.configuration?.languageEvaluationEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'languageEvaluationValidityInDays',
        label: t('candidatePane.languageEvaluationValidityInDays') as string,
        description: t('candidatePane.languageEvaluationValidityInDaysDescription') as string,
        value: account?.configuration?.languageEvaluationValidityInDays ?? 180,
        unit: t('candidatePane.days') as string,
        min: 0,
        max: 3650,
        type: 'number' as const,
      },
      {
        key: 'maxImageLimit',
        label: t('candidatePane.maxImageLimit') as string,
        description: t('candidatePane.maxImageLimitDescription') as string,
        value: account?.configuration?.maxImageLimit ?? 5,
        unit: t('candidatePane.count') as string,
        min: 0,
        max: 50,
        type: 'number' as const,
      },
      {
        key: 'webApplyEnabled',
        label: t('candidatePane.webApplyEnabled') as string,
        description: t('candidatePane.webApplyEnabledDescription') as string,
        value: account?.configuration?.webApplyEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'automaticLanguageEvaluationEnabled',
        label: t('candidatePane.automaticLanguageEvaluationEnabled') as string,
        description: t('candidatePane.automaticLanguageEvaluationEnabledDescription') as string,
        value: account?.configuration?.automaticLanguageEvaluationEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'candidateReapplicationPeriodInDays',
        label: t('candidatePane.candidateReapplicationPeriodInDays') as string,
        description: t('candidatePane.candidateReapplicationPeriodInDaysDescription') as string,
        value: account?.configuration?.candidateReapplicationPeriodInDays ?? 90,
        unit: t('candidatePane.days') as string,
        min: 0,
        max: 365,
        type: 'number' as const,
      },
      {
        key: 'seekerInFlightDurationInHours',
        label: t('candidatePane.seekerInFlightDurationInHours') as string,
        description: t('candidatePane.seekerInFlightDurationInHoursDescription') as string,
        value: account?.configuration?.seekerInFlightDurationInHours ?? 24,
        unit: t('candidatePane.hours') as string,
        min: 0,
        max: 168,
        type: 'number' as const,
      },
      {
        key: 'scheduleMatchEnabled',
        label: t('candidatePane.scheduleMatchEnabled') as string,
        description: t('candidatePane.scheduleMatchEnabledDescription') as string,
        value: account?.configuration?.scheduleMatchEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'seekerMaxHoursMatchingEnabled',
        label: t('candidatePane.seekerMaxHoursMatchingEnabled') as string,
        description: t('candidatePane.seekerMaxHoursMatchingEnabledDescription') as string,
        value: account?.configuration?.seekerMaxHoursMatchingEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'shouldEnableAppliedPositions',
        label: t('candidatePane.shouldEnableAppliedPositions') as string,
        description: t('candidatePane.shouldEnableAppliedPositionsDescription') as string,
        value: account?.configuration?.shouldEnableAppliedPositions ?? false,
        type: 'boolean' as const,
      },
    ],
    [account, t],
  );

  return (
    <GenericSettingsPane ref={genericPaneRef} onSave={onSave} setDirty={setDirty} showInlineActions={showActions} items={items} />
  );
});
